import { useMutation, useQuery } from "@tanstack/react-query";
import { App } from "antd";
import { useNavigate } from "react-router";
import { api, unwrap, type ChannelPlatformName } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";

export const PLATFORMS: ChannelPlatformName[] = ["shopee", "tokopedia", "tiktok", "lazada", "blibli"];

/** Marketplaces without a consent page: the store connects with its API key (KeyConnectModal). */
export const KEY_PLATFORMS: ChannelPlatformName[] = ["blibli"];

export function usePlatforms() {
  const { can } = useAuth();
  return useQuery({
    queryKey: ["channels", "platforms"],
    queryFn: () => unwrap(api.GET("/v1/channels/platforms")),
    enabled: can("channels.view"),
    staleTime: 5 * 60_000,
  });
}

export function useConnections(poll = false) {
  const { can } = useAuth();
  return useQuery({
    queryKey: ["channels", "connections"],
    queryFn: () => unwrap(api.GET("/v1/channels/connections")),
    enabled: can("channels.view"),
    // While a store is syncing the list refreshes itself, so "Menyinkronkan"
    // turns into "Terhubung" without a reload.
    refetchInterval: (q) =>
      poll || q.state.data?.items.some((c) => c.status === "syncing" || c.status === "connecting") ? 5000 : false,
  });
}

/**
 * Starts the OAuth round-trip: the API returns the marketplace consent URL and
 * the browser leaves the app. The marketplace sends it back to the callback,
 * which lands on /app/channels with the outcome in the query string. Key
 * platforms open their form on the Channel page instead.
 */
export function useConnect() {
  const { message } = App.useApp();
  const errorText = useErrorText();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (platform: ChannelPlatformName) =>
      KEY_PLATFORMS.includes(platform)
        ? { url: `/app/channels?connect=${platform}` }
        : unwrap(api.POST("/v1/channels/{platform}/authorize", { params: { path: { platform } } })),
    onSuccess: ({ url }) => (url.startsWith("/app/") ? void navigate(url) : window.location.assign(url)),
    onError: (err) => void message.error(errorText(err)),
  });
}
